import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatApiErrorDetail } from '../../utils/axios';
import { AdminSidebar } from './AdminLeaderboardWinners';
import { LogoUploader } from '../../components/LogoUploader';
import { Plus, Edit2, Trash2, X, Save, Trophy } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyTeam = { name: '', short_name: '', country_id: '', league: '', group_name: '', description: '', founded_year: '', fifa_code: '', logo: '', status: 'active' };
const emptyWebsite = { name: '', short_name: '', logo: '', url: '', description: '', display_order: 0, status: 'active' };

export const AdminTeams = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTeam);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/teams?limit=200`, { withCredentials: true });
      setTeams(data.teams || []);
    } catch (e) { setTeams([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyTeam); setShowForm(true); };
  const openEdit = (t) => {
    setEditing(t.id);
    setForm({
      name: t.name || '', short_name: t.short_name || '',
      country_id: t.country?.id || '', league: t.league || '',
      group_name: t.group_name || '', description: '',
      founded_year: '', fifa_code: '', logo: t.logo || '', status: t.status || 'active',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Nama tim wajib'); return; }
    const payload = { ...form };
    if (payload.country_id === '') delete payload.country_id;
    else payload.country_id = parseInt(payload.country_id);
    if (payload.founded_year === '') delete payload.founded_year;
    else payload.founded_year = parseInt(payload.founded_year);

    try {
      if (editing) {
        await axios.put(`${API_BASE}/admin/teams/${editing}`, payload, { withCredentials: true });
        toast.success('Tim diperbarui');
      } else {
        await axios.post(`${API_BASE}/admin/teams`, payload, { withCredentials: true });
        toast.success('Tim dibuat');
      }
      setShowForm(false);
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal menyimpan');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus tim ini?')) return;
    try {
      await axios.delete(`${API_BASE}/admin/teams/${id}`, { withCredentials: true });
      toast.success('Tim dihapus');
      fetchAll();
    } catch (e) { toast.error('Gagal menghapus'); }
  };

  return (
    <div className="min-h-screen bg-[#0B1020]" data-testid="admin-teams-page">
      <AdminSidebar active="/admin/teams" />
      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black text-white mb-2 flex items-center gap-3"><Trophy className="text-[#FFD60A]" size={36} /> Tim</h1>
            <p className="text-gray-400">Kelola tim yang bisa dipilih di jadwal pertandingan</p>
          </div>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-team-btn">
            <Plus size={18} /> Tambah Tim
          </button>
        </div>

        <div className="glass-card p-6">
          {loading ? <div className="text-gray-400">Loading...</div> :
           teams.length === 0 ? <p className="text-gray-400 text-center py-8">Belum ada tim</p> :
           <div className="overflow-x-auto">
             <table className="w-full text-sm">
               <thead className="border-b border-white/10 text-gray-400 text-left">
                 <tr><th className="py-3 pr-3">Logo</th><th className="py-3 pr-3">Nama</th><th className="py-3 pr-3">Short</th><th className="py-3 pr-3">Liga</th><th className="py-3 pr-3">Negara</th><th className="py-3 pr-3">Status</th><th className="py-3">Aksi</th></tr>
               </thead>
               <tbody className="divide-y divide-white/5 text-white">
                 {teams.map(t => (
                   <tr key={t.id}>
                     <td className="py-3 pr-3">
                       {t.logo ? (
                         <img src={t.logo.startsWith('http') ? t.logo : `${process.env.REACT_APP_BACKEND_URL}${t.logo}`} alt={t.name} className="w-10 h-10 object-contain" />
                       ) : (
                         <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center text-xs text-gray-500">-</div>
                       )}
                     </td>
                     <td className="py-3 pr-3 font-semibold">{t.name}</td>
                     <td className="py-3 pr-3 text-gray-400">{t.short_name || '-'}</td>
                     <td className="py-3 pr-3 text-gray-400">{t.league || '-'}</td>
                     <td className="py-3 pr-3 text-gray-400">{t.country?.name || '-'}</td>
                     <td className="py-3 pr-3"><span className="text-xs px-2 py-1 rounded bg-white/10">{t.status}</span></td>
                     <td className="py-3"><div className="flex gap-2">
                       <button onClick={() => openEdit(t)} className="p-2 rounded bg-white/5 hover:bg-white/10 text-blue-400"><Edit2 size={14} /></button>
                       <button onClick={() => handleDelete(t.id)} className="p-2 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                     </div></td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>}
        </div>

        {showForm && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal-content p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-black text-white">{editing ? 'Edit' : 'Tambah'} Tim</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Nama Tim *</label>
                  <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="form-team-name" />
                </div>
                <LogoUploader
                  value={form.logo}
                  onChange={(url) => setForm({...form, logo: url})}
                  folder="teams"
                  label="Logo Tim"
                  dataTestId="team-logo"
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Nama Pendek</label>
                    <input value={form.short_name} onChange={(e) => setForm({...form, short_name: e.target.value})} placeholder="MU" />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">FIFA Code</label>
                    <input value={form.fifa_code} onChange={(e) => setForm({...form, fifa_code: e.target.value})} placeholder="ENG" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Liga</label>
                    <input value={form.league} onChange={(e) => setForm({...form, league: e.target.value})} placeholder="Premier League" />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Group</label>
                    <input value={form.group_name} onChange={(e) => setForm({...form, group_name: e.target.value})} />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={handleSave} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-team-btn">
                    <Save size={18} /> Simpan
                  </button>
                  <button onClick={() => setShowForm(false)} className="btn-secondary">Batal</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export const AdminWebsites = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyWebsite);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/websites`, { withCredentials: true });
      setItems(data);
    } catch (e) { setItems([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyWebsite); setShowForm(true); };
  const openEdit = (w) => {
    setEditing(w.id);
    setForm({
      name: w.name || '', short_name: w.short_name || '', logo: w.logo || '',
      url: w.url || '', description: w.description || '',
      display_order: w.display_order || 0, status: w.status || 'active',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Nama website wajib'); return; }
    try {
      if (editing) {
        await axios.put(`${API_BASE}/admin/websites/${editing}`, form, { withCredentials: true });
        toast.success('Website diperbarui');
      } else {
        await axios.post(`${API_BASE}/admin/websites`, form, { withCredentials: true });
        toast.success('Website dibuat');
      }
      setShowForm(false);
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal menyimpan');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus website ini?')) return;
    try {
      await axios.delete(`${API_BASE}/admin/websites/${id}`, { withCredentials: true });
      toast.success('Website dihapus');
      fetchAll();
    } catch (e) { toast.error('Gagal menghapus'); }
  };

  return (
    <div className="min-h-screen bg-[#0B1020]" data-testid="admin-websites-page">
      <AdminSidebar active="/admin/websites" />
      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black text-white mb-2">Website Prediksi</h1>
            <p className="text-gray-400">Kelola daftar website yang muncul di dropdown prediksi</p>
          </div>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-website-btn">
            <Plus size={18} /> Tambah Website
          </button>
        </div>

        <div className="glass-card p-6">
          {loading ? <div className="text-gray-400">Loading...</div> :
           items.length === 0 ? <p className="text-gray-400 text-center py-8">Belum ada website</p> :
           <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
             {items.map(w => (
               <div key={w.id} className="glass-card p-4 bg-white/5">
                 {w.logo && (
                   <img src={w.logo.startsWith('http') ? w.logo : `${process.env.REACT_APP_BACKEND_URL}${w.logo}`} alt={w.name} className="h-12 w-full object-contain mb-3" />
                 )}
                 <div className="font-bold text-white mb-1">{w.name}</div>
                 <div className="text-xs text-gray-400 mb-2">Order: {w.display_order} · <span className={w.status === 'active' ? 'text-green-400' : 'text-gray-500'}>{w.status}</span></div>
                 {w.description && <div className="text-xs text-gray-500 mb-3">{w.description}</div>}
                 <div className="flex gap-2">
                   <button onClick={() => openEdit(w)} className="flex-1 px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-blue-400 text-xs flex items-center justify-center gap-1"><Edit2 size={12} /> Edit</button>
                   <button onClick={() => handleDelete(w.id)} className="flex-1 px-2 py-1 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs flex items-center justify-center gap-1"><Trash2 size={12} /> Hapus</button>
                 </div>
               </div>
             ))}
           </div>}
        </div>

        {showForm && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal-content p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-black text-white">{editing ? 'Edit' : 'Tambah'} Website</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Nama *</label>
                  <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="form-website-name" />
                </div>
                <LogoUploader
                  value={form.logo}
                  onChange={(url) => setForm({...form, logo: url})}
                  folder="logos"
                  label="Logo Website"
                  dataTestId="website-logo"
                />
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">URL Website</label>
                  <input value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Deskripsi</label>
                  <textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Urutan</label>
                    <input type="number" value={form.display_order} onChange={(e) => setForm({...form, display_order: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Status</label>
                    <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
                      <option value="active">Aktif</option>
                      <option value="inactive">Tidak Aktif</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={handleSave} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-website-btn">
                    <Save size={18} /> Simpan
                  </button>
                  <button onClick={() => setShowForm(false)} className="btn-secondary">Batal</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
